import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { FacebookShareButton } from 'react-share';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import { platformName } from '../platformMeta';
import { useSmartLinkShareUrl } from '../hooks/useSmartLinkShareUrl';
import { PostTimeField } from './PostTimeField';
import { ManualShareTools } from './ManualShareTools';
import type { PostStateActionsProps } from './dayPanelTypes';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the panel; each part uses its own slice of it */
import styles from './DayPanel.module.scss';
/* eslint-enable css-modules/no-unused-class */

/**
 * A post that needs the artist (design b1): the platform cannot post it for them. The panel
 * gives the file and the caption, the artist posts by hand and presses "I posted it". The post
 * can also move to a new time (owner rule) or be canceled.
 */
const NeedsYouActionsComponent: React.FC<PostStateActionsProps> = ({ post, actions, onEditInComposer }) => {
  const intl = useIntl();
  const [isTimeOpen, setTimeOpen] = useState(false);
  const shareUrl = useSmartLinkShareUrl(post.smartLinkId);
  const platform = platformName(post.platform);

  const confirm = useCallback(() => {
    actions.markPosted(post.uuid).catch(console.error);
  }, [actions, post.uuid]);
  const cancel = useCallback(() => {
    actions.cancel(post.uuid).catch(console.error);
  }, [actions, post.uuid]);
  const move = useCallback(
    (at: Date) => {
      actions
        .move(post.uuid, at)
        .then((result) => (result.ok ? setTimeOpen(false) : undefined))
        .catch(console.error);
    },
    [actions, post.uuid]
  );

  return (
    <>
      <div className={styles.dpBody}>
        <p className={styles.why}>
          <b>
            {intl.formatMessage(
              { id: 'app.sml.promote.calendar.needsYou.why', defaultMessage: '{platform} cannot post this for you.' },
              { platform }
            )}
          </b>{' '}
          {intl.formatMessage(
            {
              id: 'app.sml.promote.calendar.needsYou.how',
              defaultMessage:
                'Post it by hand in the {platform} app, then tell us, so the Calendar and Performance count it.',
            },
            { platform }
          )}
        </p>
        <ManualShareTools post={post} />
        {isTimeOpen ? (
          <PostTimeField
            scheduledAt={post.scheduledAt}
            label={intl.formatMessage({ id: 'app.sml.promote.calendar.time.label', defaultMessage: 'Time' })}
            isSaving={actions.isWriting}
            onSave={move}
          />
        ) : null}
      </div>
      <div className={styles.dpActs}>
        {post.platform === 'facebook' && shareUrl !== undefined ? (
          <FacebookShareButton url={shareUrl} className="btn btn-sm btn-outline-primary w-100">
            {intl.formatMessage({
              id: 'app.sml.promote.share.facebook',
              defaultMessage: 'Open Facebook with your page link',
            })}
          </FacebookShareButton>
        ) : null}
        <SMLButton color="primary" omitCap stretch disabled={actions.isWriting} onClick={confirm}>
          {intl.formatMessage({ id: 'app.sml.promote.share.confirm', defaultMessage: 'I posted it' })}
        </SMLButton>
        <div className={styles.row2}>
          <SMLButton size="sm" outline omitCap onClick={() => onEditInComposer(post)}>
            {intl.formatMessage({ id: 'app.sml.promote.calendar.editInComposer', defaultMessage: 'Edit in Composer' })}
          </SMLButton>
          <SMLButton size="sm" outline omitCap onClick={() => setTimeOpen((current) => !current)}>
            {intl.formatMessage({ id: 'app.sml.promote.calendar.changeTime', defaultMessage: 'Change the time' })}
          </SMLButton>
        </div>
        <button type="button" className={styles.linkbtn} disabled={actions.isWriting} onClick={cancel}>
          {intl.formatMessage({ id: 'app.sml.promote.post.cancelPost', defaultMessage: 'Cancel this post' })}
        </button>
      </div>
    </>
  );
};

export const NeedsYouActions = TypedMemo(NeedsYouActionsComponent);
